/**
 * The chat panel's pure decisions, and nothing else.
 *
 * This module has ZERO imports, touches no browser global, reads no clock and generates no
 * randomness of its own. Clocks and randomness are INJECTED by the caller, which is what makes every
 * function here deterministic under test. It mirrors `src/api/sseFrameParser.ts`'s register
 * deliberately, for the same three reasons and one more:
 *
 *  1. `ChatContext.tsx` is the highest-risk client file in this phase, and its three riskiest rules
 *     are pure decisions that would otherwise be trapped inside a React provider reachable only by a
 *     browser walkthrough. Extracted here they are repeatable assertions instead of a manual step
 *     somebody has to remember to redo (T-38-100).
 *  2. The Vike SSR build imports the panel's transport on the server. A module with nothing to
 *     evaluate cannot take a route down at import time.
 *  3. Nothing here throws. A policy question answered with an exception would turn a rendering
 *     decision into a crash, and the whole point of these functions is that they are total.
 *
 * The context DELEGATES to these functions rather than re-deriving them. In particular the 3000ms
 * reconnect threshold exists in exactly one place, which is this file.
 */

// ─── D-09: when the visitor is TOLD about a reconnect ─────────────────────────

/**
 * The reconnect visibility threshold, in milliseconds.
 *
 * This is the ONLY copy of the number. `ChatContext.tsx` must not restate it: a second copy is a
 * second thing to keep in step with the suite below, and the failure mode is silent (the banner the
 * design contract rejects reappears and no test notices).
 */
const RECONNECT_VISIBLE_AFTER_MS = 3000;

/**
 * The reconnect attempt from which the visitor may be shown anything at all. Attempts 1 and 2 are
 * VISUALLY SILENT by contract (38-UI-SPEC § Interaction State 7).
 */
const RECONNECT_VISIBLE_FROM_ATTEMPT = 3;

export type ReconnectVisibilityInput = {
  /** Which retry this is, 1-based. Attempt 1 is the first retry AFTER the original stream dropped. */
  attempt: number;
  /** How long the visitor has been staring at an unchanging partial answer. */
  gapMs: number;
  /** Whether a turn is actually in flight. A reconnect between questions is nobody's business. */
  turnInFlight: boolean;
};

/**
 * D-09's visibility rule, and the reason this module exists.
 *
 * WHEN TO TELL, NOT WHEN TO RETRY. These are two different questions and merging them is the mistake
 * this comment exists to prevent. WHEN TO RETRY lives in `src/api/chatStreamClient.ts`: 500ms
 * doubling to an 8000ms ceiling over a bounded attempt budget. WHEN TO TELL is here, and the two
 * numbers are chosen so the first two retries complete INSIDE the silence: 500ms then 1.5s, both
 * under 3s. A future reader who collapses the two will reintroduce the "reconnecting" banner the
 * design contract rejects outright, because the transport's own first retry fires at 500ms and any
 * rule keyed on "are we retrying?" is true immediately.
 *
 * Returns `true` only when all three hold: a turn is in flight, this is the third retry or later,
 * AND the gap has exceeded 3s. Non-finite inputs answer `false`, because "show nothing" is the safe
 * side of this decision: the worst case is a visitor who waits a moment longer, versus a visitor
 * told the connection is broken while it is quietly being fixed.
 */
export const shouldShowReconnectIndicator = ({
  attempt,
  gapMs,
  turnInFlight,
}: ReconnectVisibilityInput): boolean => {
  if (turnInFlight !== true) return false;
  if (typeof attempt !== 'number' || !Number.isFinite(attempt)) return false;
  if (typeof gapMs !== 'number' || !Number.isFinite(gapMs)) return false;
  if (attempt < RECONNECT_VISIBLE_FROM_ATTEMPT) return false;
  return gapMs > RECONNECT_VISIBLE_AFTER_MS;
};

// ─── The client half of exactly-once ─────────────────────────────────────────

export type MessageKeyRegistry = {
  /**
   * The key for the send being composed. Mints one on first call and returns THE SAME key on every
   * subsequent call until `completeSend()`.
   */
  keyForSend: () => string;
  /** Retire the current key. The next `keyForSend()` mints a fresh one. */
  completeSend: () => void;
};

export type MessageKeyRegistryInput = {
  /**
   * The id source. `ChatContext` injects the browser's own UUID generator
   * (`() => crypto.randomUUID()`); a test injects a counter. Injected rather than read here so this
   * module stays clock-free and randomness-free and the lifecycle is assertable without a stub.
   */
  generate: () => string;
};

/**
 * The client half of criterion 4 (one composed message produces exactly one turn).
 *
 * The server spends TWO namespaced idempotency keys off this one id (plan 38-10): `web:send:<id>` is
 * the send gate, which turns a duplicate into a visible replay of the in-flight buffer instead of a
 * silent drop, and `web:msg:<id>` is the message dedup, which is what actually holds the turn count
 * at one. BOTH depend on the client sending a STABLE id across a retry, so this registry's stability
 * invariant is the precondition for the whole exactly-once story — a fresh id per attempt would buy
 * the visitor two answers to one question, and neither server-side key could tell.
 *
 * Holds no timers, keeps no reference to anything but the current key, and never throws.
 */
export const createMessageKeyRegistry = ({
  generate,
}: MessageKeyRegistryInput): MessageKeyRegistry => {
  let current: string | null = null;

  return {
    keyForSend: (): string => {
      // The stability that makes a double-tap one turn. A second press, a manual retry and a
      // reconnect are all the same composed message and must all carry the same id.
      if (current === null) current = generate();
      return current;
    },
    completeSend: (): void => {
      // A genuinely new message is a new turn, so the next call must mint.
      current = null;
    },
  };
};

// ─── D-11 / D-12: the two identity transitions ───────────────────────────────

export const identityTransitionMap = {
  /** Stamp the RUNNING conversation with the account. Do not splice, do not reload (D-11). */
  attribute: 'attribute',
  /** Drop the stored session, the last-seen mark and the local thread (D-12). */
  reset: 'reset',
  /** Nothing to do. */
  none: 'none',
} as const;

export type IdentityTransition =
  (typeof identityTransitionMap)[keyof typeof identityTransitionMap];

export type IdentityTransitionInput = {
  previousToken: string | null | undefined;
  nextToken: string | null | undefined;
};

/** Absent means absent: unset, null, or the empty string a cleared context can leave behind. */
const isPresent = (token: string | null | undefined): boolean =>
  typeof token === 'string' && token.length > 0;

/**
 * D-11 and D-12 as ONE total function over the token pair.
 *
 * Making it total is the point. The two transitions that matter are a single comparison apart, and an
 * `if` ladder inside a React effect hides that relationship and puts it out of a test's reach. Four
 * cases, all named:
 *
 *  - absent then present  -> `attribute`. Login mid-conversation: forward the JWT so the SERVER
 *    stamps the running conversation. The thread continues as-is.
 *  - present then absent  -> `reset`. Logout on a possibly shared computer.
 *  - present then a DIFFERENT token -> `attribute`. The server's write is idempotent, so re-stamping
 *    is both safe and cheaper than reasoning about account switches from the client.
 *  - unchanged, including absent to absent -> `none`.
 *
 * The client never asserts an identity: it forwards a token and the server verifies it (T-38-77).
 */
export const resolveIdentityTransition = ({
  previousToken,
  nextToken,
}: IdentityTransitionInput): IdentityTransition => {
  const had = isPresent(previousToken);
  const has = isPresent(nextToken);

  if (!had && !has) return identityTransitionMap.none;
  if (!had && has) return identityTransitionMap.attribute;
  if (had && !has) return identityTransitionMap.reset;
  if (previousToken === nextToken) return identityTransitionMap.none;
  return identityTransitionMap.attribute;
};

// ─── D-08: peeling the interrupted marker off a turn's content ────────────────

export type InterruptedTurnInput = {
  /** The turn's content, exactly as the server persisted it. Authoritative. */
  content: string;
  /** The turn's `intent`. Only the literal `interrupted` opts into the peel. */
  intent: string | null | undefined;
};

export type InterruptedTurnParts = {
  /** The partial answer, unchanged. Renders as ordinary body text in the bubble. */
  body: string;
  /** The marker line, or `null` when there is nothing to peel. Renders as a muted footer. */
  marker: string | null;
};

/**
 * The intent this function keys on. Written once, here, because the whole reload path depends on the
 * value matching what plan 38-06 pre-declared in `DISPLAY_INTENT_ALLOWLIST` and what plan 38-11
 * persists — a second spelling anywhere in the client is a footer that silently stops appearing.
 */
const INTERRUPTED_INTENT = 'interrupted';

/**
 * Split a stopped turn into the partial the visitor keeps and the marker line that says it stopped.
 *
 * WHY THE CLIENT PEELS RATHER THAN APPENDS. The marker rides INSIDE `content`, appended server-side
 * on its own line (`orchestratorService.ts`: `${visible}\n${WEB_INTERRUPTED_MARKER}`), because
 * editing the model-history builder would have touched frozen v1 internals. So it replays into the
 * next turn's history for free, it survives a reload, and the panel must NOT append a marker of its
 * own — doing that would show it twice on the second render of the same turn.
 *
 * THE PEEL IS GATED ON THE INTENT, NOT ON THE STRING. This function never compares against the
 * Arabic marker. A client that recognised the marker by its text would start rendering an ordinary
 * answer's last line as a footer the day a model happened to end on that sentence, and would stop
 * working the day the server's wording was revised. The intent is the signal; `content` is data.
 *
 * TOTAL, and every branch keeps the partial. An absent or unrecognised intent returns the content
 * whole (the marker then reads as ordinary body text and nothing breaks, which is the older-turn
 * case the design contract calls out); a stop that landed before the first delta arrives as the
 * marker alone with no newline, and returns an empty body rather than swallowing the marker.
 */
export const splitInterruptedTurn = ({
  content,
  intent,
}: InterruptedTurnInput): InterruptedTurnParts => {
  const text = typeof content === 'string' ? content : '';
  if (intent !== INTERRUPTED_INTENT) return { body: text, marker: null };

  const breakAt = text.lastIndexOf('\n');
  if (breakAt === -1) {
    // The marker stands alone: the visitor stopped the run before any text arrived. `ChatTurn.content`
    // is required and rejects `''`, which is why the server writes the marker by itself here.
    const only = text.trim();
    return only === '' ? { body: text, marker: null } : { body: '', marker: only };
  }

  const marker = text.slice(breakAt + 1).trim();
  // A trailing newline with nothing after it is not a marker. Returning the content whole is the
  // conservative branch: a visitor never loses text, they only lose a footer.
  if (marker === '') return { body: text, marker: null };
  return { body: text.slice(0, breakAt), marker };
};

// ─── D-04: the unread dot's gate ─────────────────────────────────────────────

export type UnreadDotInput = {
  /** The server's count, or `null` / `undefined` while the request is still in flight. */
  unreadCount: number | null | undefined;
  /** Whether the stored last-seen mark has been read yet. Storage access is the caller's business. */
  lastSeenResolved: boolean;
};

/**
 * Whether to render the unread dot: `null` = render NOTHING yet, `false` = no dot, `true` = a dot.
 *
 * The tri-state is the whole content of this function. Both inputs resolve asynchronously (one is a
 * fetch, the other a guarded storage read), and collapsing "not yet known" into `false` produces the
 * exact artefact the design contract forbids — a dot that pops into the launcher a beat after
 * hydration. The launcher renders no dot at all until both are resolved.
 *
 * A dot, never a count (D-04): a number invites a badge-clearing compulsion the thread does not
 * reward.
 */
export const resolveUnreadDot = ({
  unreadCount,
  lastSeenResolved,
}: UnreadDotInput): boolean | null => {
  if (lastSeenResolved !== true) return null;
  if (typeof unreadCount !== 'number' || !Number.isFinite(unreadCount)) return null;
  return unreadCount > 0;
};
